import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Link } from 'react-router-dom';
import { Star, Quote, Plus, X, CheckCircle2, Send, ChevronRight, ChevronLeft, Phone } from 'lucide-react';
import { testimonialsApi } from '../api/testimonials';
import './Testimonials.css';

const LUXURY_FALLBACK_REVIEWS = [
  { id: 'f1', nameAr: 'أحمد محمود', nameEn: 'Ahmed Mahmoud', roleAr: 'رجل أعمال', roleEn: 'Businessman', textAr: 'تجربة استثنائية من البداية للنهاية. احترافية عالية في التصميم ودقة متناهية في مواعيد التسليم.', textEn: 'Exceptional experience from start to finish. High professionalism in design and strict adherence to deadlines.', rating: 5, initial: 'A' }
];

export function Testimonials() {
    const { language } = useLanguage();
    const isRtl = language === 'ar';
    const L = (ar, en) => isRtl ? ar : en;
    
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [formData, setFormData] = useState({ name: '', role: '', institution: '', message: '', rating: 5 });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        let isMounted = true;
        const fetchReviews = async () => {
            try {
                const data = await testimonialsApi.getAll();
                if (isMounted) {
                    const active = Array.isArray(data) ? data.filter(t => t?.isActive) : [];
                    setReviews(active.length > 0 ? active.sort((a,b) => (a?.order || 0) - (b?.order || 0)) : LUXURY_FALLBACK_REVIEWS);
                }
            } catch (err) {
                if (isMounted) setReviews(LUXURY_FALLBACK_REVIEWS);
            } finally {
                if (isMounted) setLoading(false);
            }
        };
        fetchReviews();
        return () => { isMounted = false; };
    }, []);

    const validateForm = () => {
        const newErrors = {};
        const nameVal = formData.name.trim();
        const roleVal = formData.role.trim();
        const instVal = formData.institution.trim();
        const msgVal = formData.message.trim();

        // ── STRICT VALIDATION LOGIC ──
        if (nameVal.length < 3) {
            newErrors.name = L('الاسم يجب أن يكون 3 أحرف على الأقل', 'Name must be at least 3 characters');
        } else if (!/^[\p{L}\s]+$/u.test(nameVal)) {
            newErrors.name = L('الاسم يجب أن يحتوي على حروف فقط', 'Name must contain letters only');
        }

        if (roleVal && roleVal.length < 2) {
            newErrors.role = L('المنصب يجب أن يكون حرفين على الأقل', 'Role must be at least 2 characters');
        } else if (roleVal && !/^[\p{L}\s]+$/u.test(roleVal)) {
            newErrors.role = L('المنصب يجب أن يحتوي على حروف فقط', 'Role must contain letters only');
        }

        if (instVal && instVal.length < 2) {
            newErrors.institution = L('الجهة يجب أن تكون حرفين على الأقل', 'Institution must be at least 2 characters');
        }

        if (msgVal.length < 8) {
            newErrors.message = L('الرسالة يجب أن تكون 8 أحرف على الأقل', 'Message must be at least 8 characters');
        } else if (/([^\p{L}\p{N}\s])\1{3,}/u.test(msgVal)) {
            newErrors.message = L('يرجى تجنب تكرار الرموز', 'Avoid repetitive symbols');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        setIsSubmitting(true);
        try {
            await testimonialsApi.create({
                clientName: formData.name.trim(),
                clientRole: formData.role.trim() || L('عميل', 'Client'),
                institutionName: formData.institution.trim(),
                message: formData.message.trim(),
                rating: formData.rating,
                isActive: false
            });
            setIsSuccess(true);
            setTimeout(() => { 
                setIsModalOpen(false); 
                setIsSuccess(false); 
                setFormData({ name: '', role: '', institution: '', message: '', rating: 5 }); 
                setErrors({});
            }, 3000);
        } catch (err) { 
            console.error(err);
        } finally { 
            setIsSubmitting(false); 
        }
    };

    if (loading) return <div className="lux-loader"><div className="lux-spinner"></div></div>;

    return (
        <main className="lux-tst-page" dir={isRtl ? 'rtl' : 'ltr'}>
            <section className="lux-hero-simple">
                <div className="lux-container" style={{ textAlign: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', marginBottom: '25px' }}>
                        <div style={{ backgroundColor: 'rgba(230, 57, 70, 0.1)', padding: '6px 18px', borderRadius: '50px', display: 'flex', alignItems: 'center', gap: '8px', border: '1px solid rgba(230, 57, 70, 0.2)' }}>
                            <Link to="/" style={{ color: '#e63946', textDecoration: 'none', fontWeight: '800', fontSize: '13px' }}>
                                {L('الرئيسية', 'Home')}
                            </Link>
                            <span style={{ color: '#64748b' }}>
                                {isRtl ? <ChevronLeft size={12} /> : <ChevronRight size={12} />}
                            </span>
                            <span style={{ color: '#ffffff', fontWeight: '700', fontSize: '13px' }}>
                                {L('آراء العملاء', 'Testimonials')}
                            </span>
                        </div>
                    </div>
                    <h1 className="lux-hero-title-simple">{L('شركاء النجاح', 'Success Partners')}</h1>
                    <button onClick={() => {setIsModalOpen(true); setErrors({});}} style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', padding: '16px 36px', backgroundColor: '#d4af37', color: '#fff', borderRadius: '50px', border: 'none', fontSize: '17px', fontWeight: '800', cursor: 'pointer', boxShadow: '0 10px 25px rgba(212, 175, 55, 0.4)', marginTop: '20px' }}>
                        <Plus size={22} /> {L('أضف تقييمك الآن', 'Add Your Review')}
                    </button>
                </div>
            </section>

            <section className="lux-tst-section">
                <div className="lux-container">
                    <div className="lux-tst-grid">
                        {reviews.map((rev, idx) => (
                            <div key={rev.id || idx} className="lux-tst-card">
                                <Quote className="lux-tst-bg-quote" size={120} />
                                <div className="lux-tst-content-wrapper">
                                    <div className="lux-tst-stars">{[...Array(rev.rating || 5)].map((_, i) => <Star key={i} size={16} fill="#d4af37" stroke="none" />)}</div>
                                    <p className="lux-tst-message">"{rev.message || rev.textAr}"</p>
                                    <div className="lux-tst-client-info">
                                        <div className="lux-tst-avatar">{(rev.clientName || 'A').charAt(0)}</div>
                                        <div>
                                            <h4 className="lux-tst-name">{rev.clientName || rev.nameAr}</h4>
                                            <p className="lux-tst-role">{rev.institutionName ? `${rev.clientRole} @ ${rev.institutionName}` : rev.clientRole}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="lux-tst-cta-block">
                        <div className="cta-icon-box">
                            <Quote size={40} fill="#d4af37" color="#d4af37" />
                        </div>
                        <div className="cta-text">
                            <h3>{L('آراء عملائنا هي سر قوتنا وتطورنا المستمر', 'Our clients\' opinions are our secret strength.')}</h3>
                            <p>{L('هل أنت جاهز لتكون شريكنا القادم في النجاح؟', 'Ready to be our next partner in success?')}</p>
                        </div>
                        <div className="cta-actions">
                            <Link to="/contact" className="cta-btn-lux">
                                {L('تواصل معنا الآن', 'Contact Us Now')}
                            </Link>
                            <a href="tel:01022267372" className="cta-btn-lux phone-gold">
                                <Phone size={20} />
                                <span>01022267372</span>
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {isModalOpen && (
                <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(10px)', zIndex: 999999, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '15px' }}>
                    <div style={{ backgroundColor: '#fff', borderRadius: '0', padding: '40px 30px', width: '100%', maxWidth: '550px', position: 'relative', maxHeight: '95vh', overflowY: 'auto', border: '2px solid #d4af37' }}>
                        <button onClick={() => setIsModalOpen(false)} style={{ position: 'absolute', top: '15px', [isRtl?'left':'right']: '15px', background: '#f1f5f9', border: 'none', width: '35px', height: '35px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><X size={20} color="#111" /></button>

                        {isSuccess ? (
                            <div style={{ textAlign: 'center', padding: '40px 0' }}>
                                <CheckCircle2 size={80} color="#d4af37" style={{ margin: '0 auto 20px' }} />
                                <h2 style={{color:'#111'}}>{L('تم الإرسال بنجاح!', 'Sent Successfully!')}</h2>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                                <h2 style={{ textAlign: 'center', color:'#111', fontSize: '24px', fontWeight: '800', marginBottom: '10px' }}>{L('قيم تجربتك', 'Rate Experience')}</h2>
                                
                                <div className="lux-form-group">
                                    <label style={{display:'block', marginBottom:'6px', fontWeight:'700', fontSize:'13px', color:'#555'}}>{L('الاسم بالكامل', 'Full Name')} *</label>
                                    <input style={{ width: '100%', padding: '14px', border: errors.name ? '2px solid #e63946' : '1px solid #ddd', borderRadius: '0', outline: 'none' }} value={formData.name} onChange={e=>setFormData({...formData, name: e.target.value})} placeholder={L('أدخل اسمك...', 'Enter your name...')} />
                                    {errors.name && <small style={{color:'#e63946', fontWeight:'700', marginTop:'4px', display:'block'}}>{errors.name}</small>}
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                                    <div>
                                        <label style={{display:'block', marginBottom:'6px', fontWeight:'700', fontSize:'13px', color:'#555'}}>{L('المنصب', 'Role')}</label>
                                        <input style={{ width: '100%', padding: '14px', border: errors.role ? '2px solid #e63946' : '1px solid #ddd', borderRadius: '0' }} value={formData.role} onChange={e=>setFormData({...formData, role: e.target.value})} placeholder={L('مهندس، مستثمر...', 'Architect...')} />
                                        {errors.role && <small style={{color:'#e63946', fontWeight:'700'}}>{errors.role}</small>}
                                    </div>
                                    <div>
                                        <label style={{display:'block', marginBottom:'6px', fontWeight:'700', fontSize:'13px', color:'#555'}}>{L('الجهة', 'Company')}</label>
                                        <input style={{ width: '100%', padding: '14px', border: errors.institution ? '2px solid #e63946' : '1px solid #ddd', borderRadius: '0' }} value={formData.institution} onChange={e=>setFormData({...formData, institution: e.target.value})} placeholder={L('اسم الشركة...', 'Company name...')} />
                                        {errors.institution && <small style={{color:'#e63946', fontWeight:'700'}}>{errors.institution}</small>}
                                    </div>
                                </div>

                                <div>
                                    <label style={{display:'block', marginBottom:'6px', fontWeight:'700', fontSize:'13px', color:'#555'}}>{L('التقييم', 'Rating')}</label>
                                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', direction: 'ltr' }}>
                                        {[1,2,3,4,5].map(s => <Star key={s} size={30} fill={s<=formData.rating?'#d4af37':'none'} stroke="#d4af37" style={{cursor:'pointer'}} onClick={()=>setFormData({...formData, rating: s})} />)}
                                    </div>
                                </div>

                                <div>
                                    <label style={{display:'block', marginBottom:'6px', fontWeight:'700', fontSize:'13px', color:'#555'}}>{L('رأيك في خدماتنا', 'Your Review')} *</label>
                                    <textarea rows="4" style={{ width: '100%', padding: '14px', border: errors.message ? '2px solid #e63946' : '1px solid #ddd', borderRadius: '0', resize:'none' }} value={formData.message} onChange={e=>setFormData({...formData, message: e.target.value})} placeholder={L('اكتب تجربتك هنا...', 'Write your review here...')} />
                                    {errors.message && <small style={{color:'#e63946', fontWeight:'700', marginTop:'4px', display:'block'}}>{errors.message}</small>}
                                </div>

                                <button type="submit" disabled={isSubmitting} style={{ width: '100%', padding: '18px', background: '#111', color: '#fff', border: 'none', fontWeight: '800', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '1px' }}>
                                    {isSubmitting ? '...' : L('إرسال التقييم', 'Submit Review')}
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            )}
        </main>
    );
}
